/**
 * Carbon footprint readout: a chip with the total, expanding to the per-material breakdown.
 *
 * The presentational half of SXCarbonFootprint, with no dependency beyond React — no i18next, no icon
 * set, no store — so an app that has none of those (sx-beam, React 17, its own localisation) renders
 * the same readout. The app hands in the carbon, the status of its calculation, a translate function
 * and the chevron icons. Styles: styling/scss/components/SXCarbonFootprint.scss.
 *
 * `carbon` is the project's `output.carbon`, one ElementCarbonFootprint per element; all elements are
 * summed (summariseCarbon), so a project of several elements or spans reads as one total.
 *
 * The chip is always mounted, also when there is no result — before the first calculation, between a
 * reset and its result, when a recalculation fails, and when a calculation ran but returned no carbon
 * (a material no service covers, or missing emission data). An empty readout says "this element has
 * a carbon figure, it just is not calculated right now", which a widget that disappears does not.
 */

import React from "react";
import {
  CarbonStatus,
  ElementCarbonJson,
  declaredUnitLabel,
  formatFactor,
  formatKg,
  formatQuantity,
  materialLabelKey,
  summariseCarbon,
} from "../../helpers/carbonFootprint";

/**
 * Translates a key (`carbon_footprint_`, `material_`, …, the app's own key format without any
 * `.label` suffix), falling back to `fallback` — or to the key — when the app has no translation.
 */
export type CarbonTranslate = (key: string, fallback?: string) => string;

export interface SXCarbonFootprintViewProps {
  carbon: ElementCarbonJson[] | null | undefined;
  status: CarbonStatus | string;
  translate: CarbonTranslate;
  /** Total mass [kg] of the elements, shown beside the carbon; left out when 0. */
  weightKg?: number;
  /** Placement is the app's: it differs with each app's toolbars and panels. */
  className?: string;
  style?: React.CSSProperties;
  expandIcon?: React.ReactNode;
  collapseIcon?: React.ReactNode;
}

/** Small leaf mark. The shared icon set has no environmental icon yet. */
const LeafMark = ({ size = 14 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 16 16" aria-hidden="true" focusable="false">
    <path
      d="M13.5 2.5c0 5.5-2.8 8.6-7 8.6-1 0-1.8-.2-2.4-.5C4.6 6.9 7.6 4.2 13.5 2.5Z"
      fill="currentColor"
      opacity="0.85"
    />
    <path d="M3 14c0-3.4 1.4-6.1 4-8" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
  </svg>
);

const Chevron = ({ up }: { up: boolean }) => (
  <svg width={12} height={12} viewBox="0 0 16 16" aria-hidden="true" focusable="false">
    <path
      d={up ? "M3 10.5 8 5.5l5 5" : "M3 5.5l5 5 5-5"}
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export const SXCarbonFootprintView = ({
  carbon,
  status,
  translate: t,
  weightKg = 0,
  className,
  style,
  expandIcon,
  collapseIcon,
}: SXCarbonFootprintViewProps) => {
  const [expanded, setExpanded] = React.useState(false);

  const summary = React.useMemo(() => summariseCarbon(carbon), [carbon]);
  const hasResult = summary.rows.length > 0 || summary.gwp !== 0;
  // A recalculation for the input change the user just made is in flight, and the previous figure
  // has already been reset — so this is "the number is coming", not "the number is stale".
  const updating = status === "loading";

  const title = t("carbon_footprint_", "Carbon footprint");
  const unit = t("kg_co2e_", "kg CO₂e");
  const updatingLabel = t("carbon_updating_", "Updating…");
  const noResultLabel = t("carbon_no_result_", "Run a code check to get the carbon footprint");
  const missingLabel = t(
    "carbon_missing_data_",
    "Emission data is missing for one or more materials — the total is incomplete",
  );

  return (
    <div className={`sx-carbon${className ? ` ${className}` : ""}`} style={style}>
      <button
        type="button"
        className={`sx-carbon__chip${expanded ? " sx-carbon__chip--expanded" : ""}`}
        onClick={() => setExpanded((v) => !v)}
        // Nothing to expand before the first calculation, but the chip stays clickable so the
        // explanation in the panel is reachable.
        aria-expanded={expanded}
        aria-busy={updating}
        title={updating ? updatingLabel : hasResult ? title : noResultLabel}
      >
        <span className="sx-carbon__mark">
          <LeafMark />
        </span>

        <span className={`sx-carbon__figure${updating ? " sx-carbon__figure--updating" : ""}`}>
          <span className="sx-carbon__value">{hasResult ? formatKg(summary.gwp) : updating ? "…" : "–"}</span>
          <span className="sx-carbon__unit">{unit}</span>
        </span>

        {summary.hasMissingData && (
          <span className="sx-carbon__warning" title={missingLabel}>
            !
          </span>
        )}

        <span className="sx-carbon__chevron">
          {expanded ? (collapseIcon ?? <Chevron up />) : (expandIcon ?? <Chevron up={false} />)}
        </span>

        {hasResult && (
          <span className="sx-carbon__bar" aria-hidden="true">
            {summary.rows.map((row) => (
              <span key={row.key} style={{ width: `${row.share * 100}%`, background: row.color }} />
            ))}
          </span>
        )}
      </button>

      {expanded && (
        <div className="sx-carbon__card">
          <div className="sx-carbon__card-header">
            <span className="sx-carbon__card-title">{title}</span>
            <span className="sx-carbon__card-total">
              {hasResult ? formatKg(summary.gwp) : "–"} {unit}
            </span>
          </div>

          {!hasResult && <div className="sx-carbon__empty">{updating ? updatingLabel : noResultLabel}</div>}

          {weightKg > 0 && (
            <div className="sx-carbon__weight">
              {t("element_weight_", "Weight")}: {formatKg(weightKg)} kg
            </div>
          )}

          {hasResult && (
            <>
              <table className="sx-carbon__table">
                <thead>
                  <tr>
                    <th>{t("material_", "Material")}</th>
                    <th className="text-end">{t("quantity_", "Quantity")}</th>
                    <th className="text-end">{t("carbon_factor_", "Factor")}</th>
                    <th className="text-end">{unit}</th>
                    <th className="text-end">%</th>
                  </tr>
                </thead>
                <tbody>
                  {summary.rows.map((row) => (
                    <tr key={row.key}>
                      <td>
                        <span className="sx-carbon__swatch" style={{ background: row.color }} />
                        {t(materialLabelKey(row.type))}
                        {row.materialName && <span className="sx-carbon__grade">{row.materialName}</span>}
                      </td>
                      <td className="text-end">
                        {formatQuantity(row.quantity)} {declaredUnitLabel(row.unit)}
                      </td>
                      <td className="text-end">
                        {formatFactor(row.factor)}
                        <span className="sx-carbon__per-unit">/{declaredUnitLabel(row.unit)}</span>
                      </td>
                      <td className="text-end">{formatKg(row.gwp)}</td>
                      <td className="text-end">{Math.round(row.share * 100)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr>
                    <td>{t("total_", "Total")}</td>
                    <td />
                    <td />
                    <td className="text-end">{formatKg(summary.gwp)}</td>
                    <td className="text-end">100</td>
                  </tr>
                </tfoot>
              </table>

              {/* Stored biogenic carbon, and only for materials that have any (wood) — reported as a
                  negative number, since it is carbon taken out of the atmosphere.

                  Deliberately NOT a row of the table: both design services report it *beside* the
                  A1-A3 figure and never inside it (see the emission-data notes in
                  Helper/CarbonFootprint.cs and codes/sscd000c/carbon_footprint.cpp), so the two are
                  separate quantities rather than a total and its part. As a tfoot row under
                  "Total … 100 %" it read as a subtotal of a sum it is not in — which for a timber
                  column means a −380 sitting under a 19,3 that does not contain it. */}
              {summary.gwpBiogenic !== 0 && (
                <div className="sx-carbon__biogenic">
                  {t("carbon_biogenic_", "Biogenic carbon stored (not included)")}: {formatKg(summary.gwpBiogenic)}{" "}
                  {unit}
                </div>
              )}

              {summary.hasMissingData && <div className="sx-carbon__missing">{missingLabel}</div>}

              {/* The source string from the emission database normally already names the module
                  it covers ("… module A1-A3"), so the generic scope line is only shown when
                  there is no source to state it. */}
              {summary.source ? (
                <div className="sx-carbon__source">{summary.source}</div>
              ) : (
                <div className="sx-carbon__scope">{t("carbon_scope_", "Embodied carbon, product stage A1–A3")}</div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default SXCarbonFootprintView;
